import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import FeeSettingsFields from '../components/FeeSettingsFields';
import InviteConsentsModal from '../components/InviteConsentsModal';
import VenueEditFields from '../components/VenueEditFields';
import { getFeeSourceLabel } from '../constants/feeSourceLabels';
import { venueService } from '../services/api';
import apiService from '../services/apiService';

function centsToEurString(cents) {
  const safeCents = Number.isFinite(cents) ? cents : 0;
  return (safeCents / 100).toFixed(2);
}

const formatFeeSourceLabel = (source) => getFeeSourceLabel(source, '-');

const getDefaultFeeLabel = (venue) => {
  const contract = venue?.tenant?.contractOffers?.[0];
  if (contract && Number.isFinite(contract.feePercent)) {
    return `Contratto legacy (${contract.feePercent}%)`;
  }
  // new plan-based system
  const sub = venue?.tenant?.subscriptions?.[0];
  if (sub?.planContractOffer?.feePercentSnapshot != null) {
    return `Offerta commerciale (${sub.planContractOffer.feePercentSnapshot}%)`;
  }
  if (sub?.planVersion?.feePercent != null) {
    return `Piano base (${sub.planVersion.feePercent}%)`;
  }
  return 'Global settings';
};

const VenueDetail = () => {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [feeSettings, setFeeSettings] = useState(null);
  const [feeLoading, setFeeLoading] = useState(true);
  const [feeError, setFeeError] = useState(null);
  const [feeSuccessMessage, setFeeSuccessMessage] = useState(null);
  const [feeSaving, setFeeSaving] = useState(false);
  const [feeForm, setFeeForm] = useState({
    soontipFeePercent: 0,
    soontipFeeFixedEur: '0.00'
  });

  // ── Consents modal state ──────────────────────────────────────────────────
  const [userConsents, setUserConsents] = useState(null);
  const [consentModalOpen, setConsentModalOpen] = useState(false);
  const [consentModalUser, setConsentModalUser] = useState(null); // { email, fullName, consents[] }

  // ── Edit info state ────────────────────────────────────────────────────────
  const [editForm, setEditForm] = useState({ name: '', address: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [editSuccess, setEditSuccess] = useState(null);
  const [venueCategories, setVenueCategories] = useState([]);
  const [pendingCategorySelection, setPendingCategorySelection] = useState({ hasPendingCategorySelection: false, categoryName: null });
  const [pendingCategoryModalOpen, setPendingCategoryModalOpen] = useState(false);
  const [pendingCategorySelectionResetKey, setPendingCategorySelectionResetKey] = useState(0);

  const [distributionRule, setDistributionRule] = useState(null);
  const [distributionRuleLoading, setDistributionRuleLoading] = useState(true);
  const [distributionRuleError, setDistributionRuleError] = useState(null);

  const loadVenueData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Loading venue:', venueId);
      // Carica dettagli completi venue (ADMIN endpoint - include tutto)
      const venueData = await venueService.getVenue(venueId);
      console.log('✅ Venue data received:', venueData);

      if (!venueData) {
        throw new Error('Venue data is null or undefined');
      }

      setVenue(venueData);
      setEditForm({
        name: venueData.name || '',
        address: venueData.address || ''
      });
    } catch (err) {
      console.error('❌ Failed to load venue data:', err);
      console.error('Error details:', err.response?.data || err.message);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadFeeSettings = async () => {
    try {
      setFeeLoading(true);
      setFeeError(null);

      const data = await venueService.getVenueAdminSettings(venueId);
      setFeeSettings(data);

      setFeeForm({
        soontipFeePercent: data?.soontipFeePercent ?? 0,
        soontipFeeFixedEur: centsToEurString(data?.soontipFeeFixedCents)
      });
    } catch (err) {
      console.error('❌ Failed to load venue admin-settings:', err);
      setFeeError(err.response?.data?.error || err.message);
    } finally {
      setFeeLoading(false);
    }
  };

  const handleSaveFeeOverride = async () => {
    try {
      setFeeSaving(true);
      setFeeError(null);
      setFeeSuccessMessage(null);

      await venueService.updateVenueAdminSettings(venueId, {
        soontipFeePercent: feeForm.soontipFeePercent,
        soontipFeeFixedEur: feeForm.soontipFeeFixedEur
      });

      await loadFeeSettings();

      setFeeSuccessMessage('✅ Commissione venue salvata correttamente');
    } catch (err) {
      console.error('❌ Failed to save venue admin-settings:', err);
      setFeeError(err.response?.data?.error || err.message);
    } finally {
      setFeeSaving(false);
    }
  };

  const handleResetFeeToDefault = async () => {
    const confirmed = window.confirm(
      `Vuoi ripristinare la fee di default?\n\nIl locale tornerà a usare: ${getDefaultFeeLabel(venue)}.`
    );
    if (!confirmed) return;

    try {
      setFeeSaving(true);
      setFeeError(null);
      setFeeSuccessMessage(null);

      await venueService.resetVenueAdminSettings(venueId);
      await loadFeeSettings();

      setFeeSuccessMessage('✅ Fee ripristinata ai default');
    } catch (err) {
      console.error('❌ Failed to reset venue admin-settings:', err);
      setFeeError(err.response?.data?.error || err.message);
    } finally {
      setFeeSaving(false);
    }
  };

  const loadDistributionRule = async () => {
    try {
      setDistributionRuleLoading(true);
      setDistributionRuleError(null);
      const response = await venueService.getVenueDistributionRules(venueId);
      const payload = response?.data || response;
      // payload = { active: ruleObj|null, draft: ruleObj|null, history: [] }
      setDistributionRule(payload || null);
    } catch (err) {
      setDistributionRuleError(err.response?.data?.error || err.message);
      setDistributionRule(null);
    } finally {
      setDistributionRuleLoading(false);
    }
  };

  useEffect(() => {
    loadVenueData();
    loadFeeSettings();
    loadDistributionRule();
    apiService.getVenueCategories()
      .then((response) => setVenueCategories(response.data || []))
      .catch(() => setVenueCategories([]));
    venueService.getVenueUserConsents(venueId)
      .then((data) => setUserConsents(data))
      .catch((err) => console.error('Failed to load user consents:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId]);

  const openConsentModal = (userEntry) => {
    setConsentModalUser(userEntry);
    setConsentModalOpen(true);
  };

  const findConsentsByUserId = (category, userId) => {
    if (!userConsents || !userId) return null;
    return (userConsents[category] || []).find((entry) => entry.userId === userId) || null;
  };

  const handleVenueUpdated = (updatedVenue) => {
    if (!updatedVenue?.id) return;

    const category = venueCategories.find((item) => item.id === updatedVenue.venueCategoryId) || null;
    setVenue((current) => ({
      ...current,
      ...updatedVenue,
      venueCategory: category
        ? { id: category.id, name: category.name, slug: category.slug }
        : null,
    }));
    setEditSuccess(null);
  };

  const formatDistributionModeLabel = (mode) => {
    if (mode === 'WORKER_PERCENTAGE') return 'Percettore %';
    if (mode === 'TEAM_PERCENTAGE') return 'Team %';
    return 'Standard';
  };

  const performSaveInfo = async () => {
    try {
      setEditSaving(true);
      setEditError(null);
      setEditSuccess(null);

      await venueService.updateVenue(venueId, {
        name: editForm.name,
        address: editForm.address,
      });

      await loadVenueData();
      setEditSuccess('✅ Informazioni aggiornate correttamente');
    } catch (err) {
      setEditError(err.response?.data?.error || err.message);
    } finally {
      setEditSaving(false);
    }
  };

  const handleSaveInfo = async () => {
    if (pendingCategorySelection.hasPendingCategorySelection) {
      setPendingCategoryModalOpen(true);
      return;
    }

    await performSaveInfo();
  };

  const handleDiscardPendingCategoryAndSave = async () => {
    setPendingCategoryModalOpen(false);
    setPendingCategorySelection({ hasPendingCategorySelection: false, categoryName: null });
    setPendingCategorySelectionResetKey((value) => value + 1);
    await performSaveInfo();
  };

  if (loading) return <div className="loading">Caricamento dati locale...</div>;
  if (error) return <div className="error">❌ {error}</div>;
  if (!venue) return <div className="error">Locale non trovato</div>;

  // I dati sono già inclusi dalla query admin
  const teams = venue.teams || [];
  const workers = venue.workers || [];
  const devices = venue.devices || [];
  const venueManagers = venue.venueManagers || [];
  const teamManagers = venue.teamManagers || [];

  return (
    <div>
      {/* Header del locale */}
      <div className="venue-header">
        <div className="venue-info">
          <h2>🏪 {venue.name}</h2>
          <p>📍 {venue.address}</p>
          <p><strong>🏢 Tenant:</strong> {venue.tenant?.company_name}</p>
          <p><strong>🆔 ID:</strong> {venue.id}</p>
          <p><strong>📅 Creato:</strong> {venue.createdAt ? new Date(venue.createdAt).toLocaleString('it-IT') : '-'}</p>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate(-1)}
        >
          Indietro
        </button>
      </div>

      {error && <div className="error" style={{ background: '#fee', color: '#c00', padding: '10px' }}>{error}</div>}

      {/* Statistiche */}
      <div className="card">
        <h3>📊 Statistiche Venue</h3>
        <div className="grid grid-3">
          <div className="stat-card">
            <h4>👥 Team</h4>
            <span className="stat-number">{teams.length}</span>
          </div>
          <div className="stat-card">
            <h4>👷 Workers</h4>
            <span className="stat-number">{workers.length}</span>
          </div>
          <div className="stat-card">
            <h4>📱 Devices</h4>
            <span className="stat-number">{devices.length}</span>
          </div>
          <div className="stat-card">
            <h4>🧑‍💼 Venue Managers</h4>
            <span className="stat-number">{venueManagers.length}</span>
          </div>
          <div className="stat-card">
            <h4>👤 Team Managers</h4>
            <span className="stat-number">{teamManagers.length}</span>
          </div>
        </div>
      </div>

      {/* Modifica Locale */}
      <div className="card">
        <h3>✏️ Modifica Locale</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, maxWidth: 520 }}>
          <VenueEditFields
            formData={editForm}
            onChange={(field, value) => setEditForm(f => ({ ...f, [field]: value }))}
            currentCategoryId={venue?.venueCategory?.id || venue?.venueCategoryId || null}
            currentCategoryName={venue?.venueCategory?.name || null}
            venueId={venue?.id || null}
            venueName={venue?.name || null}
            categories={venueCategories}
            onVenueUpdated={handleVenueUpdated}
            onPendingCategorySelectionChange={setPendingCategorySelection}
            pendingCategorySelectionResetKey={pendingCategorySelectionResetKey}
            clearNotice={() => {
              setEditSuccess(null);
              setEditError(null);
            }}
          />

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              className="btn btn-primary"
              onClick={handleSaveInfo}
              disabled={editSaving}
            >
              {editSaving ? 'Salvataggio...' : '💾 Salva'}
            </button>
          </div>

          {editError && (
            <div style={{ background: '#fee', border: '1px solid #fca5a5', color: '#b91c1c', padding: '10px 12px', borderRadius: 8 }}>
              ❌ {editError}
            </div>
          )}
          {editSuccess && (
            <div style={{ background: '#e8fff1', border: '1px solid #b7f5cc', color: '#0a5a2a', padding: '10px 12px', borderRadius: 8 }}>
              {editSuccess}
            </div>
          )}
        </div>
      </div>

      {pendingCategoryModalOpen && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '520px' }}>
            <h3>Selezione categoria non confermata</h3>
            <div style={{ color: '#475569', lineHeight: 1.7 }}>
              Hai selezionato la categoria {pendingCategorySelection.categoryName ? <strong>“{pendingCategorySelection.categoryName}”</strong> : <strong>indicata</strong>} ma non hai cliccato <strong>Associa categoria</strong>.
              <div style={{ marginTop: 10 }}>
                Se vuoi salvare solo le altre modifiche del locale, puoi <strong>scartare la selezione categoria</strong>. Altrimenti torna al form e conferma la categoria con l'azione dedicata.
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
              <button className="btn btn-secondary" onClick={() => setPendingCategoryModalOpen(false)}>
                Continua a modificare
              </button>
              <button className="btn" onClick={handleDiscardPendingCategoryAndSave} style={{ backgroundColor: '#ea580c', color: 'white', borderColor: '#ea580c' }}>
                Scarta selezione e salva
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SoonTip Fee Settings */}
      <div className="card">
        <h3>💸 Commissione SoonTip</h3>
        {feeLoading ? (
          <div className="loading">Caricamento impostazioni commissione...</div>
        ) : feeError ? (
          <div className="error">❌ {feeError}</div>
        ) : (
          <>
            <p>
              <strong>Fonte:</strong> {formatFeeSourceLabel(feeSettings?.source)}
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, maxWidth: 520 }}>
              <FeeSettingsFields
                percent={feeForm.soontipFeePercent}
                onPercentChange={(value) =>
                  setFeeForm((prev) => ({
                    ...prev,
                    soontipFeePercent: value
                  }))
                }
                fixedEur={feeForm.soontipFeeFixedEur}
                onFixedEurChange={(value) =>
                  setFeeForm((prev) => ({
                    ...prev,
                    soontipFeeFixedEur: value
                  }))
                }
                onDirty={() => setFeeSuccessMessage(null)}
                fixedInputClassName="form-input"
                fixedInputStyle={{ padding: 10 }}
              />

              <div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveFeeOverride}
                    disabled={feeSaving}
                  >
                    {feeSaving ? 'Salvataggio...' : 'Salva'}
                  </button>

                  {feeSettings?.source === 'venue' && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                      <button
                        className="btn btn-secondary"
                        onClick={handleResetFeeToDefault}
                        disabled={feeSaving}
                      >
                        ↩️ Ripristina fee default
                      </button>
                      <span style={{ fontSize: '11px', color: '#888' }}>
                        Il locale tornerà a usare: <strong>{getDefaultFeeLabel(venue)}</strong>
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {feeSuccessMessage && (
                <div
                  style={{
                    background: '#e8fff1',
                    border: '1px solid #b7f5cc',
                    color: '#0a5a2a',
                    padding: '10px 12px',
                    borderRadius: 8
                  }}
                >
                  {feeSuccessMessage}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Distribution Rules */}
      <div className="card">
        <h3>⚖️ Regola di distribuzione</h3>
        {distributionRuleLoading ? (
          <div className="loading">Caricamento regola...</div>
        ) : distributionRuleError ? (
          <div className="error">❌ {distributionRuleError}</div>
        ) : distributionRule?.active ? (
          (() => {
            const rule = distributionRule.active;
            const hasDraft = Boolean(distributionRule.draft);
            return (
              <>
                <p>
                  <strong>Modalità:</strong> {formatDistributionModeLabel(rule.mode)}
                  {hasDraft && (
                    <span style={{ marginLeft: 10, background: '#fff3cd', color: '#856404', padding: '2px 8px', borderRadius: 4, fontSize: 12 }}>
                      ✏️ Bozza in attesa
                    </span>
                  )}
                </p>
                <p><strong>Stato:</strong> Attiva</p>
                {rule.createdAt && (
                  <p><strong>Attivata il:</strong> {new Date(rule.createdAt).toLocaleString('it-IT')}</p>
                )}

                {rule.mode === 'WORKER_PERCENTAGE' && (() => {
                  const allocMap = Object.fromEntries((rule.workerAllocations || []).map((a) => [a.workerId, a.percentage]));
                  const workerIds = new Set(workers.map((w) => w.id));
                  const rows = workers.map((w) => ({
                    id: w.id,
                    label: `${w.firstName || ''} ${w.lastName || ''}`.trim() || w.email || w.id,
                    pct: allocMap[w.id] ?? 0,
                    deleted: false,
                  }));
                  const deletedRows = (rule.workerAllocations || [])
                    .filter((a) => !workerIds.has(a.workerId))
                    .map((a) => ({ id: a.workerId, label: a.nameSnapshot || a.workerId, pct: a.percentage, deleted: true }));
                  return (
                    <table className="admin-table">
                      <thead><tr><th>Worker</th><th className="col-right">Percentuale</th></tr></thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.id} style={r.pct === 0 ? { opacity: 0.45 } : {}}>
                            <td>{r.label}</td>
                            <td className="col-right">{r.pct}%</td>
                          </tr>
                        ))}
                        {deletedRows.map((r) => (
                          <tr key={r.id} style={{ opacity: 0.6, color: '#dc3545' }}>
                            <td>{r.label} <em>(eliminato)</em></td>
                            <td className="col-right">{r.pct}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                })()}

                {rule.mode === 'TEAM_PERCENTAGE' && (() => {
                  const allocMap = Object.fromEntries((rule.teamAllocations || []).map((a) => [a.teamId, a.percentage]));
                  const teamIds = new Set(teams.map((t) => t.id));
                  const rows = teams.map((t) => ({
                    id: t.id,
                    label: t.name || t.id,
                    pct: allocMap[t.id] ?? 0,
                    deleted: false,
                  }));
                  const deletedRows = (rule.teamAllocations || [])
                    .filter((a) => !teamIds.has(a.teamId))
                    .map((a) => ({ id: a.teamId, label: a.nameSnapshot || a.teamId, pct: a.percentage, deleted: true }));
                  return (
                    <table className="admin-table">
                      <thead><tr><th>Team</th><th className="col-right">Percentuale</th></tr></thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.id} style={r.pct === 0 ? { opacity: 0.45 } : {}}>
                            <td>{r.label}</td>
                            <td className="col-right">{r.pct}%</td>
                          </tr>
                        ))}
                        {deletedRows.map((r) => (
                          <tr key={r.id} style={{ opacity: 0.6, color: '#dc3545' }}>
                            <td>{r.label} <em>(eliminato)</em></td>
                            <td className="col-right">{r.pct}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                })()}
              </>
            );
          })()
        ) : distributionRule?.draft ? (
          <p><em>Nessuna regola attiva. Bozza presente: <strong>{formatDistributionModeLabel(distributionRule.draft.mode)}</strong> (non ancora attivata).</em></p>
        ) : (
          <p>Non configurato: viene applicato il comportamento di default con split equo.</p>
        )}
      </div>

      {/* Teams */}
      {teams.length > 0 && (
        <div className="card">
          <h3>👥 Team ({teams.length})</h3>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Workers</th>
                <th>Creato</th>
              </tr>
            </thead>
            <tbody>
              {teams.map(team => (
                <tr key={team.id}>
                  <td><strong>{team.name}</strong></td>
                  <td>{team._count?.userRoles || 0}</td>
                  <td>{new Date(team.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Venue Managers */}
      {venueManagers.length > 0 && (
        <div className="card">
          <h3>🧑‍💼 Venue Managers ({venueManagers.length})</h3>
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Status</th>
                <th>Creato</th>
                <th>Consensi</th>
              </tr>
            </thead>
            <tbody>
              {venueManagers.map(manager => {
                const consentEntry = findConsentsByUserId('venueManagers', manager.id);
                return (
                  <tr key={manager.id}>
                    <td>
                      {(manager.firstName || manager.lastName) && (
                        <div><strong>{[manager.firstName, manager.lastName].filter(Boolean).join(' ')}</strong></div>
                      )}
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{manager.email}</div>
                    </td>
                    <td><span className="status-badge status-active">ACTIVE</span></td>
                    <td>{new Date(manager.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="btn btn-secondary"
                        style={{ margin: 0, padding: '6px 12px', fontSize: '13px' }}
                        onClick={() => openConsentModal(consentEntry || { email: manager.email, fullName: null, consents: [] })}
                        title={consentEntry ? `${consentEntry.consents.length} consensi` : 'Nessun consenso'}
                      >
                        🛡️ {consentEntry ? consentEntry.consents.length : 0}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Team Managers */}
      {teamManagers.length > 0 && (
        <div className="card">
          <h3>👤 Team Managers ({teamManagers.length})</h3>
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Team</th>
                <th>Status</th>
                <th>Creato</th>
                <th>Consensi</th>
              </tr>
            </thead>
            <tbody>
              {teamManagers.map(manager => {
                const consentEntry = findConsentsByUserId('teamManagers', manager.id);
                return (
                  <tr key={manager.id}>
                    <td>
                      {(manager.firstName || manager.lastName) && (
                        <div><strong>{[manager.firstName, manager.lastName].filter(Boolean).join(' ')}</strong></div>
                      )}
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{manager.email}</div>
                    </td>
                    <td>{manager.team?.name || '-'}</td>
                    <td><span className="status-badge status-active">ACTIVE</span></td>
                    <td>{new Date(manager.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="btn btn-secondary"
                        style={{ margin: 0, padding: '6px 12px', fontSize: '13px' }}
                        onClick={() => openConsentModal(consentEntry || { email: manager.email, fullName: null, consents: [] })}
                        title={consentEntry ? `${consentEntry.consents.length} consensi` : 'Nessun consenso'}
                      >
                        🛡️ {consentEntry ? consentEntry.consents.length : 0}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Workers */}
      {workers.length > 0 && (
        <div className="card">
          <h3>👷 Workers ({workers.length})</h3>
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Team</th>
                <th>Status</th>
                <th>Attivo</th>
                <th>Creato</th>
                <th>Consensi</th>
              </tr>
            </thead>
            <tbody>
              {workers.map(worker => {
                const consentEntry = findConsentsByUserId('workers', worker.userId);
                return (
                  <tr key={worker.id}>
                    <td>
                      {(() => {
                        const isConnected = worker.status === 'CONNECTED';
                        const hasVerified = isConnected && (worker.verifiedFirstName || worker.verifiedLastName);
                        const displayName = hasVerified
                          ? [worker.verifiedFirstName, worker.verifiedLastName].filter(Boolean).join(' ')
                          : [worker.firstName, worker.lastName].filter(Boolean).join(' ');
                        return displayName ? (
                          <div>
                            <strong>{displayName}</strong>
                            {hasVerified && (
                              <span title="Nome verificato da Stripe" style={{ marginLeft: '5px', color: '#22c55e', fontSize: '13px' }}>✓</span>
                            )}
                          </div>
                        ) : null;
                      })()}
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{worker.email}</div>
                    </td>
                    <td>{worker.team?.name || '-'}</td>
                    <td>
                      <span className={`status-badge status-${(worker.status || 'unknown').toLowerCase()}`}>
                        {worker.status || 'UNKNOWN'}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`status-badge ${worker.active ? 'status-active' : 'status-inactive'}`}
                        title={worker.active ? 'Può ricevere mance in questo venue' : 'Disattivato: non riceve più mance, ma lo storico pagamenti resta invariato'}
                      >
                        {worker.active ? 'ATTIVO' : 'INATTIVO'}
                      </span>
                    </td>
                    <td>{new Date(worker.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="btn btn-secondary"
                        style={{ margin: 0, padding: '6px 12px', fontSize: '13px' }}
                        onClick={() => openConsentModal(consentEntry || { email: worker.email, fullName: null, consents: [] })}
                        title={consentEntry ? `${consentEntry.consents.length} consensi` : 'Nessun consenso'}
                      >
                        🛡️ {consentEntry ? consentEntry.consents.length : 0}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Devices */}
      {devices.length > 0 && (
        <div className="card">
          <h3>📱 Devices ({devices.length})</h3>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Label</th>
                <th>Setup Code</th>
                <th>Mode</th>
                <th>Status</th>
                <th>Creato</th>
              </tr>
            </thead>
            <tbody>
              {devices.map(device => (
                <tr key={device.id}>
                  <td>{device.label}</td>
                  <td><code>{device.setupCode}</code></td>
                  <td>
                    <span className={`status-badge status-${device.assignment_type === 'GLOBAL_POOL' ? 'connected' : 'invited'}`}>
                      {device.assignment_type === 'GLOBAL_POOL' ? '🌐 Global Pool' : '🎯 Team'}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge status-${(device.status || 'unknown').toLowerCase()}`}>
                      {device.status || 'UNKNOWN'}
                    </span>
                  </td>
                  <td>{new Date(device.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}


      {/* Consents modal */}
      <InviteConsentsModal
        open={consentModalOpen}
        userName={consentModalUser?.fullName || consentModalUser?.email}
        userEmail={consentModalUser?.email}
        consents={consentModalUser?.consents || []}
        onClose={() => setConsentModalOpen(false)}
      />

    </div>
  );
};

export default VenueDetail;
